import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Warning12Filled,
    CheckmarkCircle12Filled,
    Info12Regular,
    SubtractCircle12Regular,
} from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import {
    saveInsightsModuleToJournal,
    buildManagementSummaryJournalBody,
    buildInsightsItemJournalBody,
} from '../../../utils/askLensJournal';
import {
    filterSupportedInsightsActions,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import {
    resolveEvidenceObjectTarget,
    resolveEvidencePanelScopeArea,
} from '../../../utils/intelligenceInsightsModuleViewModels';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';

const RAG_CLASS = {
    amber: styles.ragAmber,
    green: styles.ragGreen,
    red: styles.ragRed,
    grey: styles.ragGrey,
};

function RagPill({ rag, label }) {
    const display =
        label && String(label).toLowerCase() !== String(rag || '').toLowerCase()
            ? label
            : String(rag || 'grey').replace(/^\w/, ch => ch.toUpperCase());
    return (
        <span className={`${styles.ragPill} ${RAG_CLASS[rag] || styles.ragGrey}`}>
            <span className={styles.ragDot} aria-hidden />
            {display}
        </span>
    );
}

function ToneIcon({ tone }) {
    if (tone === 'positive') return <CheckmarkCircle12Filled style={{ color: '#22c55e' }} aria-hidden />;
    if (tone === 'warning') return <Warning12Filled style={{ color: '#f59e0b' }} aria-hidden />;
    return <Info12Regular style={{ color: '#3b82f6' }} aria-hidden />;
}

function overallPrefill(data) {
    return [data?.overall?.headline, data?.overall?.summary, data?.companyOverview]
        .filter(Boolean)
        .join('\n\n');
}

function takeawayPrefill(item) {
    return [item?.title, item?.body].filter(Boolean).join('\n\n');
}

const IntelligenceInsightsManagementSummary = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();

    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [evidenceTarget, setEvidenceTarget] = useState(null);

    const profile = data?.profile || { green: 0, amber: 0, red: 0, grey: 0 };
    const profileTotal = profile.green + profile.amber + profile.red + profile.grey || 1;
    const overallDetail = data?.overallDetail || {};
    const overallActions = filterSupportedInsightsActions(
        data?.overall?.actions?.length
            ? data.overall.actions
            : [
                  { key: 'view_details', label: 'View details' },
                  { key: 'view_evidence', label: 'View evidence' },
                  { key: 'add_to_raid', label: 'Add to RAID' },
                  { key: 'save_to_journal', label: 'Save to Journal' },
              ],
        { moduleKey: 'management_summary' },
    );

    const handleAction = async (action, context = {}) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const contextText = context.text || '';

        if (key === 'view_details') {
            setEvidenceTarget(null);
            setDetailOpen(true);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailOpen(false);
            const target = action.target || context.evidenceTarget || {};
            const { objectType, objectId } = resolveEvidenceObjectTarget(target, context);
            if (objectType && objectId) {
                setEvidenceTarget({
                    mode: 'object',
                    objectType,
                    objectId,
                    area: resolveEvidencePanelScopeArea({
                        data,
                        target,
                        context,
                        preferredKeys: context.scopeKeys || [],
                    }),
                });
                return;
            }
            setEvidenceTarget({
                mode: 'scope',
                area: resolveEvidencePanelScopeArea({
                    data,
                    target,
                    context,
                    preferredKeys: context.scopeKeys || [],
                }),
            });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(contextText || overallPrefill(data));
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(contextText || overallPrefill(data));
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(contextText || overallPrefill(data));
            setShowObsSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Management Summary',
                    headline: context.headline || data?.overall?.headline || 'Overall assessment',
                    bodyText:
                        buildInsightsItemJournalBody(context.item) ||
                        buildManagementSummaryJournalBody(data, contextText || overallPrefill(data)),
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
        if (key === 'open_key_findings') {
            const scopeArea =
                action.target?.scope_area ||
                context.scopeArea ||
                null;
            onOpenModule?.('key_findings', scopeArea ? { scopeArea } : undefined);
            return;
        }
        if (key === 'open_evidence_follow_ups') {
            onOpenModule?.('evidence_follow_ups');
            return;
        }
        if (key === 'open_risks_signals') {
            onOpenModule?.('risks_signals');
            return;
        }
    };

    const detailSections = useMemo(() => {
        const sections = [];
        if (overallDetail.supportingStrengths?.length) {
            sections.push({
                title: 'Supporting strengths',
                items: overallDetail.supportingStrengths,
                tone: 'positive',
            });
        }
        if (overallDetail.constrainingFactors?.length) {
            sections.push({
                title: 'Constraining factors',
                items: overallDetail.constrainingFactors,
                tone: 'warning',
            });
        }
        if (overallDetail.crossCuttingImplications?.length) {
            sections.push({
                title: 'Cross-cutting implications',
                items: overallDetail.crossCuttingImplications,
                tone: 'info',
            });
        }
        if (overallDetail.assessmentLimitations?.length) {
            sections.push({
                title: 'Assessment limitations',
                items: overallDetail.assessmentLimitations,
                tone: 'info',
            });
        }
        return sections;
    }, [overallDetail]);

    const hasContent = Boolean(data && !data.isEmpty);
    if (error || !hasContent) {
        const failed =
            Boolean(error) ||
            data?.status === 'failed' ||
            data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'Management Summary'}
                subtitle={
                    data?.subtitle ||
                    'Overall technology assessment with priority takeaways and scope matrix.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="management_summary"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                demoBadge={false}>
                <section className={styles.section}>
                    <p className={styles.sectionTitle}>Company Overview</p>
                    <p className={styles.sectionText}>{data.companyOverview}</p>
                    <div className={styles.actionRow} style={{ marginTop: 10 }}>
                        <button
                            type="button"
                            className={styles.btnLink}
                            onClick={() =>
                                handleAction(
                                    { key: 'view_evidence', label: 'View evidence' },
                                    { text: data.companyOverview },
                                )
                            }>
                            View evidence
                        </button>
                    </div>
                </section>

                <section className={styles.section}>
                    <p className={styles.sectionTitle}>Overall Assessment</p>
                    <div className={styles.overallCard}>
                        <div className={styles.overallRagRow}>
                            <span className={styles.overallRagPrefix}>Overall assessment ·</span>
                            <RagPill rag={data.overall.rag} label={data.overall.ragLabel} />
                        </div>
                        <p className={styles.overallHeadline}>{data.overall.headline}</p>
                        {typeof data.overall.summary === 'string' && data.overall.summary ? (
                            <p className={styles.overallSummary}>{data.overall.summary}</p>
                        ) : null}
                        {data.overall.confidence ? (
                            <p className={styles.confidence}>{data.overall.confidence}</p>
                        ) : null}
                        <div className={styles.actionRow}>
                            {overallActions.map(action => (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={
                                        action.key === 'view_details'
                                            ? styles.btnPrimary
                                            : styles.btnOutline
                                    }
                                    onClick={() =>
                                        handleAction(action, { text: overallPrefill(data) })
                                    }>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {detailOpen ? (
                    <section className={styles.section}>
                        <p className={styles.sectionTitle}>Overall assessment detail</p>
                        <div className={styles.overallCard}>
                            {detailSections.length ? (
                                detailSections.map(section => (
                                    <div key={section.title} style={{ marginBottom: 14 }}>
                                        <p className={styles.sectionTitle}>{section.title}</p>
                                        <ul className={styles.takeawayList}>
                                            {section.items.map(item => (
                                                <li key={item} className={styles.takeawayItem}>
                                                    <ToneIcon tone={section.tone} />
                                                    <p className={styles.takeawayBody}>{item}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                <p className={styles.sectionText}>
                                    {(typeof data.overall.summary === 'string' &&
                                        data.overall.summary) ||
                                        data.overall.headline}
                                </p>
                            )}
                            <button
                                type="button"
                                className={styles.btnLink}
                                onClick={() => setDetailOpen(false)}>
                                Hide details
                            </button>
                        </div>
                    </section>
                ) : null}

                <section className={styles.section}>
                    <p className={styles.sectionTitle}>Priority Takeaways</p>
                    <div className={styles.takeawayList}>
                        {(data.takeaways || []).map((item, idx) => (
                            <div key={item.id} className={styles.takeawayItem}>
                                <span className={styles.takeawayIndex}>{idx + 1}</span>
                                <div>
                                    <div className={styles.chipRow} style={{ marginBottom: 6 }}>
                                        <ToneIcon tone={item.tone} />
                                        <span className={`${styles.chip} ${styles.materialChip}`}>
                                            {item.materiality}
                                        </span>
                                    </div>
                                    <p className={styles.takeawayTitle}>{item.title}</p>
                                    <p className={styles.takeawayBody}>{item.body}</p>
                                    <div className={styles.chipRow}>
                                        {(item.scopes || []).map(scope => (
                                            <span key={scope} className={styles.chip}>
                                                {scope}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                {item.action ? (
                                    <button
                                        type="button"
                                        className={styles.btnOutline}
                                        onClick={() =>
                                            handleAction(item.action, {
                                                text: takeawayPrefill(item),
                                                item,
                                                scopeKeys: item.scopeKeys,
                                                objectType: item.classification,
                                                objectId: item.primaryOriginId,
                                                evidenceTarget: {
                                                    object_type: item.classification,
                                                    object_id: item.primaryOriginId,
                                                },
                                            })
                                        }>
                                        {item.action.label}
                                    </button>
                                ) : (
                                    <span />
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                <section className={styles.section}>
                    <p className={styles.sectionTitle}>Assessment Profile</p>
                    <div className={styles.profileBar} aria-hidden>
                        <div
                            className={styles.profileSegGreen}
                            style={{ width: `${(profile.green / profileTotal) * 100}%` }}
                        />
                        <div
                            className={styles.profileSegAmber}
                            style={{ width: `${(profile.amber / profileTotal) * 100}%` }}
                        />
                        <div
                            className={styles.profileSegRed}
                            style={{ width: `${(profile.red / profileTotal) * 100}%` }}
                        />
                        <div
                            className={styles.profileSegGrey}
                            style={{ width: `${(profile.grey / profileTotal) * 100}%` }}
                        />
                    </div>
                    <div className={styles.profileLegend}>
                        <span>
                            <CheckmarkCircle12Filled style={{ color: '#22c55e' }} aria-hidden />{' '}
                            {profile.green} Green
                        </span>
                        <span>
                            <Warning12Filled style={{ color: '#f59e0b' }} aria-hidden /> {profile.amber}{' '}
                            Amber
                        </span>
                        <span>
                            <SubtractCircle12Regular style={{ color: '#ef4444' }} aria-hidden />{' '}
                            {profile.red} Red
                        </span>
                        <span>{profile.grey} Insufficient</span>
                    </div>
                    {data.profileNote ? (
                        <p className={styles.profileNote}>
                            <Warning12Filled aria-hidden /> {data.profileNote}
                        </p>
                    ) : null}
                </section>

                <section className={styles.section}>
                    <p className={styles.sectionTitle}>Scope Assessment</p>
                    <div className={styles.scopeGrid}>
                        {(data.scopes || []).map(scope => (
                            <article key={scope.key} className={styles.scopeCard}>
                                <div className={styles.scopeTitleRow}>
                                    <RagPill rag={scope.rag} />
                                    <h3 className={styles.scopeTitle}>{scope.label}</h3>
                                </div>
                                <p className={styles.scopeText}>{scope.text}</p>
                                {scope.unresolved ? (
                                    <p className={styles.unresolved}>
                                        <Warning12Filled aria-hidden /> {scope.unresolved} unresolved
                                    </p>
                                ) : null}
                                {scope.confidence ? (
                                    <p className={styles.confidence}>{scope.confidence}</p>
                                ) : null}
                                <div className={styles.scopeLinks}>
                                    <button
                                        type="button"
                                        className={styles.btnLink}
                                        onClick={() =>
                                            handleAction(
                                                {
                                                    key: 'open_key_findings',
                                                    label: 'Key Findings →',
                                                    target: {
                                                        module_key: 'key_findings',
                                                        scope_area: scope.keyFindingsScope || scope.key,
                                                    },
                                                },
                                                { scopeArea: scope.keyFindingsScope || scope.key },
                                            )
                                        }>
                                        Key Findings →
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.btnLink}
                                        onClick={() =>
                                            handleAction(
                                                { key: 'view_evidence', label: 'Evidence' },
                                                {
                                                    text: scope.text,
                                                    evidenceArea: scope.key,
                                                    scopeKeys: [scope.key],
                                                },
                                            )
                                        }>
                                        Evidence
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {data.footerBanner ? (
                    <div className={styles.footerBanner}>
                        <div>
                            <p className={styles.footerBannerTitle}>{data.footerBanner.title}</p>
                            <p className={styles.footerBannerMeta}>{data.footerBanner.affected}</p>
                        </div>
                        <button
                            type="button"
                            className={styles.btnLink}
                            onClick={() => onOpenModule?.(data.footerBanner.actionKey)}>
                            {data.footerBanner.actionLabel}
                        </button>
                    </div>
                ) : null}
            </InsightsModuleChrome>

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || null}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        text: panelCard?.headline || overallPrefill(data),
                        evidenceArea: evidenceTarget?.area,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsManagementSummary;
